import { createFileRoute } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { match } from 'ts-pattern';

import { DeviceType } from '@/constants/device';
import { useDeviceType } from '@/features/device/device-type/useDeviceType';
import ImportCsv from '@/features/import-csv';
import { ImportCsvSearchSchema } from '@/features/import-csv/zod-schemas';
import { defaultNS, useTranslation } from '@/lib/i18n';

import useDeviceComputerTableRows from './-computer/useDeviceComputerTableRow';
import useDeviceTypeParam from './-hooks/useDeviceTypeParam';
import useDeviceMobileTableRows from './-mobile/useDeviceMobileTableRow';
import useDeviceMonitorTableRows from './-monitor/useDeviceMonitorTableRow';
import useDeviceOtherTableRows from './-other/useDeviceOtherTableRow';
import useDeviceServerTableRows from './-server/useDeviceServerTableRow';
import { resolveDeviceCsvApiPaths } from './-utils/deviceCsvApiPaths';

const DeviceImportCsv = () => {
  const search = Route.useSearch();

  const navigate = Route.useNavigate();
  const { deviceType } = useDeviceTypeParam();

  const { t } = useTranslation(defaultNS, {
    keyPrefix: 'pages.devices.importCsv.deviceType',
  });
  const { tShort: tDeviceTypeShort } = useDeviceType();

  const { t: tCompleteDialog } = useTranslation(defaultNS, {
    keyPrefix: 'pages.devices.importCsv.completeDialog',
  });

  const { exportApiUrl } = resolveDeviceCsvApiPaths(deviceType);

  const title = match(deviceType)
    .with(DeviceType.COMPUTER, () => t('title.computer'))
    .with(DeviceType.MOBILE, () => t('title.mobile'))
    .with(DeviceType.MONITOR, () => t('title.monitor'))
    .with(DeviceType.SERVER, () => t('title.server'))
    .with(DeviceType.OTHER, () => t('title.other'))
    .exhaustive();

  const useTableRows = match(deviceType)
    .with(DeviceType.COMPUTER, () => useDeviceComputerTableRows)
    .with(DeviceType.MOBILE, () => useDeviceMobileTableRows)
    .with(DeviceType.MONITOR, () => useDeviceMonitorTableRows)
    .with(DeviceType.SERVER, () => useDeviceServerTableRows)
    .with(DeviceType.OTHER, () => useDeviceOtherTableRows)
    .exhaustive();

  const exportFilename = match(deviceType)
    .with(DeviceType.COMPUTER, () => tDeviceTypeShort('computer'))
    .with(DeviceType.MOBILE, () => tDeviceTypeShort('mobile'))
    .with(DeviceType.MONITOR, () => tDeviceTypeShort('monitor'))
    .with(DeviceType.SERVER, () => tDeviceTypeShort('server'))
    .with(DeviceType.OTHER, () => tDeviceTypeShort('other'))
    .exhaustive();

  const templateTitle = match(deviceType)
    .with(DeviceType.COMPUTER, () => t('prepareCsvFile.templateTitle.computer'))
    .with(DeviceType.MOBILE, () => t('prepareCsvFile.templateTitle.mobile'))
    .with(DeviceType.MONITOR, () => t('prepareCsvFile.templateTitle.monitor'))
    .with(DeviceType.SERVER, () => t('prepareCsvFile.templateTitle.server'))
    .with(DeviceType.OTHER, () => t('prepareCsvFile.templateTitle.other'))
    .exhaustive();

  const unit = match(deviceType)
    .with(DeviceType.COMPUTER, () => tDeviceTypeShort('computer'))
    .with(DeviceType.MOBILE, () => tDeviceTypeShort('mobile'))
    .with(DeviceType.MONITOR, () => tDeviceTypeShort('monitor'))
    .with(DeviceType.SERVER, () => tDeviceTypeShort('server'))
    .with(DeviceType.OTHER, () => tDeviceTypeShort('other'))
    .exhaustive();

  const updateExisting = match(deviceType)
    .with(DeviceType.COMPUTER, () =>
      t('prepareCsvFile.updateExisting.computer'),
    )
    .with(DeviceType.MOBILE, () => t('prepareCsvFile.updateExisting.mobile'))
    .with(DeviceType.MONITOR, () => t('prepareCsvFile.updateExisting.monitor'))
    .with(DeviceType.SERVER, () => t('prepareCsvFile.updateExisting.server'))
    .with(DeviceType.OTHER, () => t('prepareCsvFile.updateExisting.other'))
    .exhaustive();

  return (
    <ImportCsv
      baseLink={`/devices/import-csv/${deviceType}`}
      baseTitle={title}
      exportApiUrl={exportApiUrl}
      exportFilename={exportFilename}
      templateMessage={{
        templateTitle,
        updateExisting,
        completeLinkButton: tCompleteDialog('button'),
      }}
      completeLinkUrl="/devices/inventories"
      unit={unit}
      search={search}
      navigate={navigate}
      useTableRows={useTableRows}
      splitTemplateFilename
    />
  );
};

export const Route = createFileRoute('/_staff/devices/import-csv/$deviceType')({
  validateSearch: zodValidator(ImportCsvSearchSchema),
  component: DeviceImportCsv,
});
